import { resolveText } from './i18n/customer'
import type { NotificationsView, TextTrigger } from './types'
import { t, type StaffLanguage } from './staffText'

/**
 * A customer's text as their phone shows it, worked out in the browser while the
 * words are being typed.
 *
 * The server already answers each saved text filled in and priced (the view's
 * samples, internal/api/notifications.go viewNotifications). That answer is for
 * the saved words; a drawer being typed into needs the same answer for words
 * nobody has saved yet, and a round trip per keystroke would be absurd. So this is
 * the server's own arithmetic, ported: the same plausible visit, the same
 * language fallback (layout.Text.In), the sentence with the follow link dropped
 * when there is no link (notify.withoutLink), and the same segment count
 * (notify.Segments). Nothing here is invented; if the two drift, the saved text's
 * row, which shows the server's count, is the one to believe.
 */

/** What each moment may say (internal/notify/settings.go, placeholders). */
export const PLACEHOLDERS: Record<TextTrigger, readonly string[]> = {
  issued: ['number', 'branch', 'service', 'ahead', 'wait', 'follow_url'],
  ahead: ['number', 'branch', 'service', 'ahead', 'wait'],
  called: ['number', 'branch', 'service', 'desk'],
}

/** Every placeholder any moment may say, in the order the buttons offer them. */
export const ALL_PLACEHOLDERS = ['number', 'branch', 'service', 'ahead', 'wait', 'desk', 'follow_url'] as const
export type Placeholder = (typeof ALL_PLACEHOLDERS)[number]

/** The longest a moment's words may be, in characters (notify.maxTemplate). */
export const MAX_WORDS = 480

/** The highest "people ahead" a text can wait for (notify.MaxAheadAt). */
export const MAX_AHEAD = 20

/** A prefix as the server accepts it (notify.prefixShape). */
export const PREFIX_SHAPE = /^\+[0-9]{1,6}$/

/**
 * The follow link the server's samples carry, as long as a real one because its
 * length is most of the first text's cost. Read back out of the samples rather
 * than rebuilt: only the server knows its own public address. Empty when the
 * installation prints no link, and then the sentence holding it is dropped.
 */
export function sampleLink(view: Pick<NotificationsView, 'samples'>): string {
  for (const s of view.samples) {
    const found = /\S+\/t#x{22}/.exec(s.text)
    if (found) return found[0]
  }
  return ''
}

/** The visit the server fills its samples with (viewNotifications), its desk named in lang. */
export function sampleValues(link: string, lang = 'az'): Record<string, string> {
  const words: StaffLanguage = lang === 'ru' || lang === 'en' ? lang : 'az'
  const values: Record<string, string> = {
    number: 'A-042',
    branch: 'Nizami',
    service: 'Kredit',
    desk: t('org.sms.sampleDesk', words),
    ahead: '3',
    wait: '12',
  }
  if (link) values.follow_url = link
  return values
}

/** The words the server would read for a language: its own, else the organisation's
 *  default, English, any (layout.Text.InFor). Empty when none has any, which the
 *  server answers with the words it ships. */
export function wordsIn(words: Record<string, string> | undefined, lang: string, orgDefault?: string): string {
  return resolveText(words, lang, orgDefault)
}

const TOKEN = /\{\{\s*([^{}]*?)\s*\}\}/g

/** The placeholders the words use, as the server reads them. */
export function usedPlaceholders(words: string): string[] {
  return [...words.matchAll(TOKEN)].map((m) => m[1]!).filter((k) => k !== '')
}

/** The placeholders a moment may not say. */
export function refusedPlaceholders(trigger: TextTrigger, words: string): string[] {
  return [...new Set(usedPlaceholders(words))].filter((k) => !PLACEHOLDERS[trigger].includes(k))
}

/** notify.dropSentenceWith: the sentence carrying the link goes when there is
 *  no link to give. */
function dropSentenceWith(words: string, placeholder: string): string {
  const marks = /[.!?\n]/
  for (;;) {
    const i = words.indexOf(placeholder)
    if (i < 0) return words.trim()
    let start = 0
    for (let k = i - 1; k >= 0; k--) {
      if (marks.test(words[k]!)) {
        start = k + 1
        break
      }
    }
    let end = words.length
    const after = words.slice(i + placeholder.length)
    const k = after.search(marks)
    if (k >= 0) end = i + placeholder.length + k + 1
    words = words.slice(0, start).replace(/ +$/, '') + words.slice(end)
  }
}

/** One piece of a filled text: plain words, a value the visit put there, or a
 *  placeholder with no value (which the server refuses at save). */
export type Piece = { text: string; kind: 'words' | 'value' | 'link' | 'unknown' }

/** The words filled in with the sample visit, piece by piece, so a screen can
 *  show which parts change from visit to visit. */
export function fillPieces(words: string, values: Record<string, string>): Piece[] {
  const source = values.follow_url ? words : dropSentenceWith(words, '{{follow_url}}')
  const out: Piece[] = []
  let at = 0
  for (const m of source.matchAll(TOKEN)) {
    if (m.index! > at) out.push({ text: source.slice(at, m.index), kind: 'words' })
    const key = m[1]!
    const value = values[key]
    if (key === '') out.push({ text: m[0], kind: 'words' })
    else if (value !== undefined) out.push({ text: value, kind: key === 'follow_url' ? 'link' : 'value' })
    else out.push({ text: m[0], kind: 'unknown' })
    at = m.index! + m[0].length
  }
  if (at < source.length) out.push({ text: source.slice(at), kind: 'words' })
  // The server trims the finished text.
  if (out.length) {
    out[0] = { ...out[0]!, text: out[0]!.text.replace(/^\s+/, '') }
    const last = out.length - 1
    out[last] = { ...out[last]!, text: out[last]!.text.replace(/\s+$/, '') }
  }
  return out.filter((p) => p.text !== '')
}

export const filledText = (pieces: Piece[]) => pieces.map((p) => p.text).join('')

// The GSM 03.38 alphabet (notify.gsmBasic, gsmExtended). A text entirely in it
// costs 160 characters a segment; one character outside it (ə, ş, ğ, any
// Cyrillic) makes the whole text UCS-2, at 70.
const GSM_BASIC = new Set([
  ...'@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&\'()*+,-./0123456789:;<=>?¡ÄÖÑÜ§¿äöñüà',
  ...'abcdefghijklmnopqrstuvwxyz',
  ...'abcdefghijklmnopqrstuvwxyz'.toUpperCase(),
  ' ',
  '\n',
  '\r',
])
const GSM_EXTENDED = new Set([...'^{}\\[~]|€', '\f'])

/** How many SMS a text is billed as, and its length in the unit that decides it
 *  (notify.Segments): septets for GSM, UTF-16 units otherwise. */
export function segments(text: string): { parts: number; length: number; gsm: boolean } {
  if (text === '') return { parts: 0, length: 0, gsm: true }
  let septets = 0
  let gsm = true
  for (const ch of text) {
    if (GSM_BASIC.has(ch)) septets++
    else if (GSM_EXTENDED.has(ch)) septets += 2
    else gsm = false
  }
  if (gsm) return { parts: septets <= 160 ? 1 : Math.ceil(septets / 153), length: septets, gsm }
  const units = text.length
  return { parts: units <= 70 ? 1 : Math.ceil(units / 67), length: units, gsm }
}
